import { useCallback, useState, type ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { CurrentMax, Exercise, TimedSessionResult } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { useBreakpoint } from "../useBreakpoint";
import { formatShortDate } from "../Logger/formatDate";
import { DensityTrendPanel } from "../Progress/DensityTrendPanel";
import { E1RMTrendsPanel } from "../Progress/E1RMTrendsPanel";
import { ExerciseFrequencyPanel } from "../Progress/ExerciseFrequencyPanel";
import { MovementBalancePanel } from "../Progress/MovementBalancePanel";
import { ProgressCollapsibleWidget } from "../Progress/ProgressCollapsibleWidget";
import { nowToTheMinute, PROGRESS_WEEK_BARS } from "../Progress/progressWindow";
import { progressWidgetLabel } from "../Progress/progressWidgetCatalog";
import { ProgressPrTimelineCard } from "../Progress/ProgressPrTimelineCard";
import { ProgressRecordsCard } from "../Progress/ProgressRecordsCard";
import { ProgressWeekStreakCard } from "../Progress/ProgressWeekStreakCard";
import type { ProgressDeps } from "../Progress/types";
import type { UseChartWorkoutsOptions } from "../Progress/useChartWorkouts";
import { useSessionsSummary } from "../Progress/useSessionsSummary";
import { useTagVolumeRecords } from "../Progress/useTagVolumeRecords";
import { CoachMaxesCard } from "./CoachMaxesCard";
import { CoachRhythmCard } from "./CoachRhythmCard";
import { EffortCoveragePanel } from "./EffortCoveragePanel";

// The panel grid of the coach's athlete detail, split out of AthleteDetail
// for CO-29. The Progress panels render VERBATIM over the athlete-scoped deps.
// CO-28 stalest exercises, CO-10 effort coverage, rhythm and the MAX list as
// before; CO-29 adds the week streak, volume by tag and the PR timeline. The
// last two start closed and read nothing until opened, as on Progress.
// Not here: pain x volume (health data, #155), and the two panels that read
// raw sets, reps at load and the intensity heatmap: /api/sets answers only
// its owner. #25 shows the coach the sets per session instead (the Sessions
// section), not through this read.
// The host keys this on the athlete, so no state outlives them.
export function AthletePanelGrid({
  theme,
  deps,
  results,
  catalog,
  maxes,
  weekStartDay,
  locale,
  schedule,
}: {
  theme: UiTheme;
  deps: ProgressDeps;
  results: TimedSessionResult[] | null;
  catalog: ReadonlyArray<Exercise> | null;
  maxes: Map<string, CurrentMax> | null;
  weekStartDay?: number | undefined;
  locale: string;
  /** Host defer for the sessions summary and the volume-by-tag read. */
  schedule?: UseChartWorkoutsOptions["schedule"];
}) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const sessions = useSessionsSummary(deps.apiFetch, weekStartDay, schedule);
  // PG-39: the heaviest read here starts only once its card is open.
  const [tagVolumeOpened, setTagVolumeOpened] = useState(false);
  const onTagVolumeOpen = useCallback(() => setTagVolumeOpened(true), []);
  const tagVolume = useTagVolumeRecords({
    apiFetch: deps.apiFetch,
    catalog,
    weekStartDay,
    enabled: tagVolumeOpened,
    schedule,
  });
  const nowMs = nowToTheMinute();
  const closed = (label: string, node: ReactNode) => (
    <ProgressCollapsibleWidget theme={theme} label={label}>
      {node}
    </ProgressCollapsibleWidget>
  );
  const tagVolumeLabel = progressWidgetLabel(t, "tagVolume");
  const prTimelineLabel = progressWidgetLabel(t, "prTimeline");

  // [key, accessible name, panel]
  const panels: Array<[string, string, ReactNode]> = [
    ["e1rm", progressWidgetLabel(t, "e1rmTrends"), <E1RMTrendsPanel {...deps} />],
    [
      "balance",
      progressWidgetLabel(t, "movementBalance"),
      <MovementBalancePanel {...deps} />,
    ],
    // Volume (CO-1). Same relative slot as the athlete's own tab, where
    // density follows balance in the detail group.
    [
      "density",
      t("mobile.progress.density.title", { defaultValue: "Density" }),
      <DensityTrendPanel {...deps} />,
    ],
    [
      "tagVolume",
      tagVolumeLabel,
      closed(
        tagVolumeLabel,
        <ProgressRecordsCard
          tagWeeks={tagVolume.tagWeeks}
          chartError={tagVolume.chartError}
          displayUnit={deps.units}
          locale={locale}
          theme={theme}
          onExpand={onTagVolumeOpen}
        />,
      ),
    ],
    [
      "frequency",
      progressWidgetLabel(t, "exerciseFrequency"),
      <ExerciseFrequencyPanel {...deps} />,
    ],
    [
      "effort",
      t("coach.detail.effortTitle", { defaultValue: "Effort coverage" }),
      <EffortCoveragePanel theme={theme} apiFetch={deps.apiFetch} catalog={catalog} />,
    ],
    [
      "rhythm",
      progressWidgetLabel(t, "rhythm"),
      <CoachRhythmCard theme={theme} results={results} weekStartDay={weekStartDay} />,
    ],
    [
      "weekStreak",
      progressWidgetLabel(t, "weekStreak"),
      <ProgressWeekStreakCard
        theme={theme}
        streak={sessions.weekStreak}
        summaryError={sessions.error}
        weeklyCounts={sessions.weeklyCounts.slice(-PROGRESS_WEEK_BARS)}
        nowMs={nowMs}
        weekStartDay={weekStartDay}
        locale={locale}
      />,
    ],
    [
      "maxes",
      t("coach.detail.maxesTitle", { defaultValue: "Current MAX" }),
      <CoachMaxesCard theme={theme} maxes={maxes} catalog={catalog} units={deps.units} />,
    ],
    [
      "prTimeline",
      prTimelineLabel,
      closed(
        prTimelineLabel,
        <ProgressPrTimelineCard deps={deps} formatDate={formatShortDate} locale={locale} />,
      ),
    ],
  ];
  const styles = makeStyles(theme);

  // MX-8: one tree at every width, only the style changes, so a resize
  // across the breakpoint no longer remounts the panels. Each cell is a named
  // region: `role` and `aria-label` reach the DOM on web and map natively.
  return (
    <View style={wide ? styles.grid : styles.stack}>
      {panels.map(([k, label, node]) => (
        <View
          key={k}
          role="region"
          aria-label={label}
          style={wide ? styles.gridCell : null}
        >
          {node}
        </View>
      ))}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    // flexWrap 2-col on wide, single column on narrow. flex-start so a tall
    // card can't stretch its row sibling into an empty void.
    // NOT the same rules as ProgressView's panel grid (space-between +
    // rowGap + a strict width:48%), despite the identical wide?grid:column
    // mounting shape: the two DIVERGE deliberately. Registered as CN-panel-grid
    // in next/consolidation.md; collapsing them changes one live surface's
    // layout, so it needs Antenore's call, not a boy-scout merge.
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s3,
      alignItems: "flex-start",
    },
    gridCell: { flexGrow: 1, flexBasis: "48%", minWidth: 320 },
    // Narrow: one column, the same gap as AthleteDetail's `wrap`.
    stack: { gap: s.s4 },
  });
}
